<template lang="pug">
.content
  
  //- TOP NAV
  ed-title-nav(ref='header' :title='$t("Import Expense Data")')
    span(slot='action')
        router-link(v-if='$root.legacySubtab' :to='"/d/Admin/Import"')
          i.fa.fa-long-arrow-left
          span &nbsp; {{ $t('Back') + ' ' + $t('to') + ' ' + $t($root.legacySubtab) }}
  
  //- PAGE
  .container-fluid
    ed-section.active(offset)

      //- STEPS
      ed-tab-nav.col-md-12.steps.uniform(
        steps wide
        ref='steps'
        @tabSelected='enterStep'
        :check='uploadFileValid'
      )
        
        //- 1. IMPORT FILE section
        ed-tab(title="Import File")
          
          ed-section.col-md-9.active(offset=4)

            h5 Import your expense data file
            
            p
              | Imports will update existing or create new expenses based on looking at
              | the Expense IDs of existing expenses in Eved.
              | Fields in the Expense Information, Supplier Information,
              | and Bill To sections in the Expense module can be added or updated with this import.
            
            p If the imported data has more than one tab, the first tab will be imported.
            p Column heading must begin on Row 1.

            p Accepted file formats are .xls and .xlsx

            ed-form(:endpoint='api.env + api.$.expense.upload' no-submit :submit-visible='false')
              ed-upload(button-label='Browse'
                        v-model='attachedFile'
                        :helptipHeader='"Error"'
                        :helptipBody='uploadFileError'
                        :error='!uploadFileValid'
                        :showHelptipIcon='false'
                        required
                        upload-auto
                        accept='application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        extensions='xls,xlsx'
              )


        //- 2. MATCH COLUMNS section
        ed-tab(title="Match Columns" :endpoint='api.$.expense.import'
          data='data'
          :payload='data.fieldsMap'
          payloadType='json'
          feedback='errors'
          validate='checkMatched'
          )
          
          ed-section.col-md-12.active
            
            .col-md-6.col-md-offset-3
              p
                | Use the table below to match the Column Headers from Row 1 of
                | your uploaded file to match to your Expense Fields in Eved.
              p
                | Required fields are indicated with an #[strong.text-danger *]. All required fields must have
                | a column matched to them to move to Step 3.
            p

            ed-form.col-md-10.col-md-offset-1(:endpoint='api.$.expense.create' :submit-visible='false')
              table.table.table-bordered(v-show='data.fieldsMap && data.fieldsMap[0].fieldName !== null')
                thead
                  tr
                    th.col-md-3 #[br] Expense Field
                    th #[br] Column Header in File
                tbody
                  tr(v-for='(field, index) in updateMatches(data.fieldsMap, data.importedColumns)'
                    :class='{ "required": field.isRequired && !(field.columns && field.columns.length) }'
                  )
                    td {{ field.label }} #[strong.text-danger {{ field.isRequired ? '*' : '' }}]
                    td
                      ed-select(
                        :options='getImportedColumns(index)'
                        :value='columnName(field.columns, field.type === "list")'
                        :customLabel='columnName'
                        :required='field.isRequired'
                        @select='handleMultimatch(field, $event)'
                        @open='fetchImportedColumns(data.importedColumns, index)'
                      )
                        a(slot='clear'
                          v-if='field.columns && field.columns.length === 1 && field.type !== "list"'
                          href='#'
                          @click.prevent='removeMatch(field)'
                        )
                          i.fas.fa-times-circle.add-icon
                        
                        template(slot='option' slot-scope='props')
                          ed-select-options(thumbnails)
                            span(slot='row1')
                              kbd {{ props.option.letter }}
                              | &nbsp;&nbsp;
                              span {{ props.option.name || '[None]' }}
                      
                      .additional-match(v-if='field.type && field.columns')
                        .form-group(v-for='column in field.columns')
                          .col-md-6
                            kbd.pull-left {{ column.letter }}
                            small.col-md-10 {{ column.name || `[None]` }}
                          .col-md-5(v-if='initDetails(column, field)')
                            ed-select.inline.small(
                              :options='titles'
                              label='text'
                              placeholder='Title'
                              @open='fetchTitles'
                              :openEndpoint='api.$.expense.titles'
                              @select='setDetails("title", column, $event)'
                            )
                            ed-select.inline.small(
                              :options='permissions'
                              placeholder='Permission'
                              multiple
                              checkboxes
                              @open='fetchPermissions'
                              :openEndpoint='api.$.expense.permissions'
                              @select='setDetails("permission", column, $event)'
                            )
                          small.col-md-1
                            a.pull-right(href='#' @click.prevent='removeColumn(column, field)')
                              i.fas.fa-times-circle
                          br.clear
                          br

        //- 3. REVIEW ERRORS section
        ed-tab(title="Review Errors" :optional='"Import corrected file"' :errors='errors')
          
          ed-section.col-md-11.active
            .col-md-6.col-md-offset-3
              p
                | The table below shows the errors in your file. Please correct
                | these errors in your file and return to step 1 to upload the
                | corrected file.
            
            p
            p
            table.table.table-bordered.col-md-10.col-md-offset-1
              thead
                tr
                  th Row
                  th Column
                  th Data
                  th Column Header
                  th Expense Field
                  th Error Reason
              tbody
                tr(v-for='(error, i) in errors')
                  td {{ error.row }}
                  td {{ error.column }}
                  td {{ error.data }}
                  td {{ error.columnHeader }}
                  td {{ error.fieldName }}
                  td {{ error.errorReason }}


        //- ========================================= FINALIZE section
        ed-tab(title="Finalize" button-label='Ok')
          
          ed-section.col-md-9.active
            .col-md-12.col-md-offset-4
              h3
                | Import Successfully Initiated
                | &nbsp;
                i.success.inline
              p
                | No errors were found in your file and your expense data is being imported now.
                | You'll receive an email shortly when the import is complete and all data is updated in Eved.
              p
              p
              p

    //- BUTTONS
    ed-button.pull-right.btn.btn-primary(v-if='lastStep' :to='"/d/Admin/Import"') Ok
    ed-button.pull-right(v-if='!lastStep' @click='proceed' :disabled='!uploadFileValid || !attachedFile') {{ $t(nextLabel) }}
    ed-button.pull-right.btn.btn-secondary(v-if='!lastStep' :to='"/d/Admin/Import"') {{ $t('Cancel') }}

    //- MODALS
    ed-modal(
      :show='notAllRequiredMatched' 
      @close='notAllRequiredMatched = false'
      title='Check Unmatched Column Headers'
      subtitle='Please match all required Expense Fields to Column Headers in order to move on to the next step.'
      primaryButton='Ok'
      secondaryButton='false'
    )
    ed-modal(
      :show='notAllMatched' 
      @close='notAllMatched = false'
      @confirm='skipMatch'
      title='Check Unmatched Column Headers'
      subtitle='Not all of your Column Headers are matched to Expense Fields. Do you still want to continue to the next step?'
      primaryButton='Yes'
      secondaryButton='No'
    )

</template>

<script>

import { Eved, EventBus } from '@/event-bus'
import wizardMixin from '@/core/components/edTabNav/wizardMixin'
// import customTypes from './types.json'

export default {
  name: 'ImportExpenses',

  mixins: [wizardMixin],

  methods: {
    /**
     * Updates matched column/columns list
     */
    handleMultimatch(field, value) {
      if (field.type === 'list') {
        field.columns = field.columns || []
        field.columns.push(value)
      } else {
        field.columns = [value]
      }

      this.$forceUpdate()
    },

    /**
     * Removes 1:1 match
     */
    removeMatch(field) {
      field.columns = []

      this.$forceUpdate()
    },

    /**
     * Removes 1:* match
     */
    removeColumn(column, field) {
      field && field.columns.splice(field.columns.indexOf(column), 1)

      this.$forceUpdate()
    },

    /**
     * Gets permissions list
     */
    fetchPermissions() {
      this.permissions.length || this.api(this.api.$.expense.permissions).then(r => {
        this.permissions = r.data || []
      })
    },

    /**
     * Gets titles list
     */
    fetchTitles() {
      this.titles.length || this.api(this.api.$.expense.titles).then(r => {
        this.titles = r.data || []
      })
    },

    /**
     * Checks if required/all fields are matched
     */
    checkMatched() {
      if (this.data.fieldsMap[0].fieldName !== null) {
        const allRequiredMatched = this.data.fieldsMap.filter(field => field.isRequired && !(field.columns && field.columns.length)).length === 0
        const allMatched = this.canSkip || this.data.fieldsMap.filter(field => !field.isRequired && !(field.columns && field.columns.length)).length === 0

        this.notAllRequiredMatched = !allRequiredMatched
        this.notAllMatched = allRequiredMatched && !allMatched

        if (!allRequiredMatched) {
          EventBus.$emit(Eved.EXPENSE.IMPORT.INVALID)
        }

        return allRequiredMatched && allMatched
      }
    },

    /**
     * Can proceed to next step if not all not required are matched
     */
    skipMatch() {
      this.canSkip = true
      this.proceed()
      this.canSkip = false
    },

    /**
     * Callback after each step
     */
    afterEnterStep(step) {
      // Activate Admin menu
      global.Page.showTabs('Admin', 'Import', '#d', null)

      // reset any pending data
      this.$store.state.pendingData = null

      if (step) {
        EventBus.$emit(Eved.EXPENSE.IMPORT.ENTERED_STEP, step)
      }
    }
  },

  created() {
    this.importedColumns = []
  },

  data () {
    return {
      canSkip: false,
      notAllMatched: false,
      notAllRequiredMatched: false,

      data: {
        fieldsMap: [
          {
            fieldName: null,
            isRequired: true,
            columns: []
          }
        ],
        importedColumns: []
      },

      errors: null,

      permissions: [],
      titles: [],

      /**
       * Column match label
       */
      columnName(option, multiple) {
        if (!option) return
        if (option.length === 0) return ''

        //
        if (multiple === false) {
          return option
        }

        // 1:* match
        if (multiple) return option && option.length + ' selected'

        // 1:1 match - [LETTER] NAME
        return option && typeof option === 'string'
                ? option
                : `[${option.letter || option[0] && option[0].letter}] ${option.name || option[0] && option[0].name || '[None]'}`
      },

      /**
       * Set details for custom matches
       */
      initDetails(column, field) {
        /* if (field.Alias === 'Collaborators') {
          column.Details = column.Details || {}
        } */

        return field.alias === 'Collaborators'
      },
      setDetails(field, column, value) {
        column.details = column.details || {}
        column.details[field] = value
      },

      /**
       * Enables/disables unselected/selected columns
       */
      updateMatches(fields, columns) {
        if (!columns.length) return fields

        const matchedFields = fields
          .map(field => field.columns)
          .reduce((fields, columns) => (fields || []).concat(columns))
          .filter(columns => columns)
          .map(column => column.letter)

        columns = columns.map(column => {
          if (~matchedFields.indexOf(column.letter)) {
            column.$isDisabled = true
          } else {
            delete column.$isDisabled
          }

          return column
        })

        return fields
      },

      /**
       * Only inject options @open dropdown
       * to avoid performance bottlenecks
       * when rendering large sets
       */
      fetchImportedColumns(columns, index) {
        this.$set(this.importedColumns, index, columns)
        this.$forceUpdate()
      },
      getImportedColumns(index) {
        return this.importedColumns[index] || []
      }
    }
  }
}
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

.table td:first-child {
  vertical-align: top;
  padding-top: 0.75em;
}

kbd {
  display: inline-block;
  margin-left: -12px;
  min-width: 25px;
  text-align: center;
  line-height: 1.1em;

  @include theme {
  background: v(secondary-01);
  }
}

.additional-match {
  > .form-group {
    float: left;
    width: 100%;
    margin-top: 5px;
    margin-bottom: 5px;

    @include theme {
    border-bottom: 1px solid v(background-light);
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/Import/expenses.vue